import React, { useEffect, useState } from 'react';
import { useApp } from '../context/AppContext';

// ── "Where should the money go?" ───────────────────────────────────────────
//
// Julian pays technicians by hand out of his bank's bill pay. Before this the
// numbers lived in a text message, a photo of a voided cheque, or his head,
// and every payment run started with going to look for them.
//
// The technician types their own. Julian never keys somebody else's account
// number, which is less work and one fewer place for a digit to go wrong: a
// transposed digit sends a man's week to a stranger.
//
// Once saved, this screen never shows the number again. Nobody needs to see
// it, the last four is enough to recognise it, and a full account number
// sitting on a screen in a van is a number somebody else can read.
export default function PayoutDetails() {
  const { vendorProfile, savePayout, isSavingPayout } = useApp();
  const saved = vendorProfile?.payout || {};
  const onFile = Boolean(saved.method);

  const [editing, setEditing] = useState(!onFile);
  // QPS pays by ACH and nothing else, so there is no method to pick. Offering
  // Zelle and a posted cheque was three questions where there is one fact, and
  // two of the three answers led somewhere QPS was never going to pay from.
  const [f, setF] = useState({
    method: 'ach',
    accountHolder: saved.accountHolder || vendorProfile?.businessName || '',
    accountType: saved.accountType || 'checking',
    bankName: saved.bankName || '',
    routingNumber: '',
    accountNumber: '',
    confirmAccount: '',
  });
  const set = (patch) => setF((p) => ({ ...p, ...patch }));

  useEffect(() => { if (!onFile) setEditing(true); }, [onFile]);

  const submit = async () => {
    // Typed twice, checked here. A bank will not tell you the number was
    // wrong, it will just bounce the payment a week later.
    if (f.accountNumber !== f.confirmAccount) {
      alert('The two account numbers do not match. Check them and try again.');
      return;
    }
    const ok = await savePayout(f);
    if (ok) {
      setEditing(false);
      setF((p) => ({ ...p, routingNumber: '', accountNumber: '', confirmAccount: '' }));
    }
  };

  if (onFile && !editing) {
    return (
      <div className="pay-card">
        <div className="pay-head">
          <span className="pay-title">How you get paid</span>
          <button type="button" className="btn btn-outline btn-sm" onClick={() => setEditing(true)}>Change</button>
        </div>
        {saved.method === 'ach' && (
          <>
            <div className="pay-row"><span>Bank transfer</span><b>{saved.bankName || 'Your bank'}</b></div>
            <div className="pay-row"><span>{saved.accountType === 'savings' ? 'Savings' : 'Checking'}</span><b>••••{saved.accountLast4}</b></div>
            <div className="pay-row"><span>In the name of</span><b>{saved.accountHolder}</b></div>
          </>
        )}
        {/* Nobody can choose these any more. Kept so a record saved before ACH
            became the only option still shows something rather than a blank
            card; the Change button reopens it as bank details. */}
        {saved.method === 'zelle' && <div className="pay-row"><span>Zelle</span><b>{saved.zelleContact}</b></div>}
        {saved.method === 'check' && (
          <>
            <div className="pay-row"><span>Cheque to</span><b>{saved.checkPayableTo}</b></div>
            <div className="pay-row"><span>Posted to</span><b>{saved.checkAddress}</b></div>
          </>
        )}
        <p className="pay-note">
          Only the last four digits are shown, here and to QPS. Nobody can read
          the full number off this screen.
        </p>
      </div>
    );
  }

  return (
    <div className="pay-card editing">
      <div className="pay-head"><span className="pay-title">Where your money goes</span></div>
      {/* .pay-note is written for the line UNDER a form, so it has no bottom
          margin. Used as an intro it ends up glued to the first label. */}
      <p className="pay-note" style={{ margin: '0 0 16px' }}>
        QPS pays by bank transfer. Fill this in and your payments land straight
        in your account.
      </p>

      <>
          <Field label="Name on the account" id="payHolder">
            <input id="payHolder" type="text" value={f.accountHolder}
              onChange={(e) => set({ accountHolder: e.target.value })} placeholder="Ruiz Drywall LLC" />
          </Field>
          <Field label="Your bank" id="payBank" hint="Zions, Chase, America First — whichever it is.">
            <input id="payBank" type="text" value={f.bankName}
              onChange={(e) => set({ bankName: e.target.value })} />
          </Field>
          <Field label="Checking or savings?" id="payType">
            <select id="payType" value={f.accountType} onChange={(e) => set({ accountType: e.target.value })}>
              <option value="checking">Checking</option>
              <option value="savings">Savings</option>
            </select>
          </Field>
          <Field label="Routing number" id="payRouting" hint="The nine digits on the bottom left of a cheque.">
            <input id="payRouting" type="text" inputMode="numeric" autoComplete="off" value={f.routingNumber}
              onChange={(e) => set({ routingNumber: e.target.value })} placeholder="124302150" />
          </Field>
          <Field label="Account number" id="payAccount">
            <input id="payAccount" type="text" inputMode="numeric" autoComplete="off" value={f.accountNumber}
              onChange={(e) => set({ accountNumber: e.target.value })} />
          </Field>
          <Field label="Type the account number again" id="payConfirm"
            hint="A bank will not tell us it was wrong. It just bounces a week later.">
            <input id="payConfirm" type="text" inputMode="numeric" autoComplete="off" value={f.confirmAccount}
              onChange={(e) => set({ confirmAccount: e.target.value })} />
          </Field>
      </>

      <p className="pay-note">
        Your account number is scrambled before it is stored. QPS sees the last
        four digits on screen, and can only look up the full number one time at
        a time, which is written down on your record.
      </p>

      <button type="button" className="pay-save" disabled={isSavingPayout} onClick={submit}>
        {isSavingPayout ? 'Saving…' : 'Save'}
      </button>
      {onFile && (
        <button type="button" className="btn btn-ghost btn-lg" onClick={() => setEditing(false)}>Cancel</button>
      )}
    </div>
  );
}

function Field({ label, id, hint, children }) {
  return (
    <div className="qps-input-block">
      <label htmlFor={id}>{label}</label>
      {hint && <p className="qps-input-hint">{hint}</p>}
      {children}
    </div>
  );
}
